import React from "react";
import _ from "underts";
import { trpc } from "../main/components/ProviderContainer.tsx";
import classNames from "classnames";
import { DateTime } from "luxon";

type TaskResult =
  | {
      result: "success";
    }
  | {
      result: "error";
      message: string;
    }
  | {
      result: "pending";
    };

const LaunchTasks = () => {
  const [hasTasksRunning, setHasTasksRunning] = React.useState(false);
  const [rebuildGrandLivreResult, setRebuildGrandLivreResult] = React.useState<{
    [year: number]: TaskResult;
  }>({});
  const [rapprocheBordereauxResult, setRapprocheBordereauxResult] =
    React.useState<TaskResult | null>(null);
  const [rapprocheFacturesResult, setRapprocheFacturesResult] =
    React.useState<TaskResult | null>(null);
  const currentYear = DateTime.now().setZone("Europe/Paris").year;
  const previousYears = _.range(5, -1, -1).map((i) => currentYear - i);
  const utils = trpc.useUtils();
  const accountingPeriodsQuery = trpc.accountingPeriod.list.useQuery();
  const accountingPeriods = accountingPeriodsQuery.data || [];
  const displayedYears = previousYears;
  const hasIncompleteClosure = accountingPeriods.some(
    (period) => period.status === "closing" || period.status === "failed",
  );

  const lockAccountingPeriodMutation =
    trpc.accountingPeriod.lockYear.useMutation({
      onSettled: () => utils.invalidate(),
    });

  const rebuildGrandLivreMutation = trpc.grandLivre.rebuild.useMutation({
    onMutate: (year) => {
      setRebuildGrandLivreResult((prev) => ({
        ...prev,
        [year]: {
          result: "pending",
        },
      }));
    },
    onSuccess: (_data, year) => {
      setRebuildGrandLivreResult((prev) => ({
        ...prev,
        [year]: {
          result: "success",
        },
      }));
    },
    onSettled: () => {
      setHasTasksRunning(false);
    },
    onError: (error, year) => {
      setRebuildGrandLivreResult((prev) => ({
        ...prev,
        [year]: {
          result: "error",
          message: error.message,
        },
      }));
    },
  });
  const rapprocheBordereauxMutation = trpc.rapprochement.bordereaux.useMutation(
    {
      onMutate: () => {
        setRapprocheBordereauxResult({
          result: "pending",
        });
      },
      onSuccess: () => {
        setRapprocheBordereauxResult({
          result: "success",
        });
      },
      onError: (error) => {
        setRapprocheBordereauxResult({
          result: "error",
          message: error.message,
        });
      },
      onSettled: () => {
        setHasTasksRunning(false);
      },
    },
  );
  const rapprocheFacturesMutation = trpc.rapprochement.factures.useMutation({
    onMutate: () => {
      setRapprocheFacturesResult({
        result: "pending",
      });
    },
    onSuccess: () => {
      setRapprocheFacturesResult({
        result: "success",
      });
    },
    onError: (error) => {
      setRapprocheFacturesResult({
        result: "error",
        message: error.message,
      });
    },
    onSettled: () => {
      setHasTasksRunning(false);
    },
  });
  const handleRebuildGrandLivre = async (year: number) => {
    setHasTasksRunning(true);
    rebuildGrandLivreMutation.mutate(year);
  };
  const handleRapprocheBordereaux = async () => {
    setHasTasksRunning(true);
    rapprocheBordereauxMutation.mutate();
  };
  const handleRapprocheFactures = async () => {
    setHasTasksRunning(true);
    rapprocheFacturesMutation.mutate();
  };
  const handleLockAccountingPeriod = (year: number) => {
    const confirmed = window.confirm(
      `Clôturer définitivement l'exercice ${year} ? Cette action est irréversible.`,
    );
    if (!confirmed) {
      return;
    }
    lockAccountingPeriodMutation.mutate(year);
  };
  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Tâches</h3>
      </div>
      <div className="card-body">
        {accountingPeriodsQuery.isLoading && (
          <div className="alert alert-info">Chargement des exercices...</div>
        )}
        {accountingPeriodsQuery.error && (
          <div className="alert alert-danger" role="alert">
            Impossible de charger l'état des exercices. Les reconstructions sont
            désactivées par sécurité.
          </div>
        )}
        {lockAccountingPeriodMutation.error && (
          <div className="alert alert-danger" role="alert">
            La clôture de l'exercice a échoué :{" "}
            {lockAccountingPeriodMutation.error.message}
          </div>
        )}
        {displayedYears.map((year) => {
          const accountingPeriod = accountingPeriods.find(
            (period) => period.year === year,
          );
          const hasAccountingPeriodState =
            !accountingPeriodsQuery.isLoading &&
            !accountingPeriodsQuery.isError;
          const isMissingHistoricalPeriod = !accountingPeriod && year <= 2025;
          const accountingPeriodStatus = isMissingHistoricalPeriod
            ? "locked"
            : accountingPeriod?.status;
          const isLocked = accountingPeriodStatus === "locked";
          const isUnavailable =
            accountingPeriodStatus !== undefined &&
            accountingPeriodStatus !== "open";
          const isStaleClosing =
            accountingPeriodStatus === "closing" &&
            accountingPeriod !== undefined &&
            DateTime.fromJSDate(accountingPeriod.updatedAt)
              .plus({ hours: 1 })
              .toMillis() < DateTime.now().toMillis();
          const canLock =
            hasAccountingPeriodState &&
            (accountingPeriodStatus === "open" ||
              accountingPeriodStatus === "failed" ||
              isStaleClosing) &&
            year <= currentYear;
          const isLocking =
            lockAccountingPeriodMutation.isPending &&
            lockAccountingPeriodMutation.variables === year;

          return (
            <div
              className="row g-2 mb-2 align-items-center"
              key={"button" + year}
            >
              <div className="col">
                <button
                  className={classNames("btn w-100", {
                    "btn-primary": !rebuildGrandLivreResult[year],
                    "btn-success":
                      rebuildGrandLivreResult[year]?.result === "success",
                    "btn-danger":
                      rebuildGrandLivreResult[year]?.result === "error",
                  })}
                  onClick={() => handleRebuildGrandLivre(year)}
                  disabled={
                    hasTasksRunning ||
                    lockAccountingPeriodMutation.isPending ||
                    accountingPeriodsQuery.isLoading ||
                    accountingPeriodsQuery.isError ||
                    isUnavailable
                  }
                >
                  Rebuild Grand Livre {year}
                </button>
              </div>
              <div className="col-auto">
                <span
                  className={classNames("badge", {
                    "text-bg-danger":
                      accountingPeriodStatus === "locked" ||
                      accountingPeriodStatus === "failed",
                    "text-bg-warning": accountingPeriodStatus === "closing",
                    "text-bg-success": accountingPeriodStatus === "open",
                    "text-bg-secondary":
                      !hasAccountingPeriodState && !isLocked,
                  })}
                >
                  {accountingPeriodStatus === "locked"
                    ? "Exercice verrouillé"
                    : accountingPeriodStatus === "closing"
                      ? "Clôture en cours"
                      : accountingPeriodStatus === "failed"
                        ? "Clôture à reprendre"
                        : accountingPeriodStatus === "open"
                          ? "Exercice ouvert"
                          : "État indisponible"}
                </span>
              </div>
              {canLock && (
                <div className="col-auto">
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    disabled={
                      hasTasksRunning || lockAccountingPeriodMutation.isPending
                    }
                    onClick={() => handleLockAccountingPeriod(year)}
                  >
                    {isLocking
                      ? "Clôture..."
                      : accountingPeriodStatus === "failed" || isStaleClosing
                        ? "Reprendre la clôture"
                        : "Clôturer définitivement"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        <div className="row" key={"rapprocheBordereauKey"}>
          <button
            className={classNames("btn", {
              "btn-primary": !rapprocheBordereauxResult,
              "btn-success": rapprocheBordereauxResult?.result === "success",
              "btn-danger": rapprocheBordereauxResult?.result === "error",
            })}
            onClick={() => handleRapprocheBordereaux()}
            disabled={
              hasTasksRunning ||
              lockAccountingPeriodMutation.isPending ||
              hasIncompleteClosure ||
              accountingPeriodsQuery.isLoading ||
              accountingPeriodsQuery.isError
            }
          >
            Rapproche Bordereaux
          </button>
        </div>
        <div className="row" key={"rapprocheFacturesKey"}>
          <button
            className={classNames("btn", {
              "btn-primary": !rapprocheFacturesResult,
              "btn-success": rapprocheFacturesResult?.result === "success",
              "btn-danger": rapprocheFacturesResult?.result === "error",
            })}
            onClick={() => handleRapprocheFactures()}
            disabled={
              hasTasksRunning ||
              lockAccountingPeriodMutation.isPending ||
              hasIncompleteClosure ||
              accountingPeriodsQuery.isLoading ||
              accountingPeriodsQuery.isError
            }
          >
            Rapproche Factures
          </button>
        </div>
      </div>
    </div>
  );
};

export default LaunchTasks;
